// StudentAnalyticsPage.js - Full-page analytics view for students
import React from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { getMainContentColors } from "../components/MainContentArea";

// Responsive breakpoints
const TABLET_BREAKPOINT = 768;
const DESKTOP_BREAKPOINT = 1024;

/**
 * StudentAnalyticsPage - Full-page analytics screen showing:
 * - Assessment history with scores
 * - Adaptive learning recommendations
 */
const StudentAnalyticsPage = ({
  assessments = [],
  adaptiveRecommendation = null,
  formatLocalDate,
  isDarkMode = false,
  t = (s) => s,
}) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= DESKTOP_BREAKPOINT;
  const isTablet = width >= TABLET_BREAKPOINT && width < DESKTOP_BREAKPOINT;

  const colors = getMainContentColors(isDarkMode);

  // Calculate overall stats
  const totalAssessments = assessments.length;
  const averageAccuracy = totalAssessments > 0
    ? Math.round(assessments.reduce((sum, a) => sum + (a.accuracy || 0), 0) / totalAssessments)
    : 0;
  const totalScore = assessments.reduce((sum, a) => sum + (a.score || 0), 0);
  const maxPossibleScore = assessments.reduce((sum, a) => sum + (a.total_score || 0), 0);

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: colors.background }]}
      contentContainerStyle={styles.contentContainer}
    >
      {/* Header */}
      <View style={styles.header}>
        <View style={[styles.headerIcon, { backgroundColor: isDarkMode ? "rgba(129, 140, 248, 0.15)" : "rgba(99, 102, 241, 0.1)" }]}>
          <MaterialCommunityIcons
            name="chart-bar"
            size={32}
            color={isDarkMode ? "#818cf8" : "#6366f1"}
          />
        </View>
        <View style={styles.headerText}>
          <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>
            {t("Performance Analytics")}
          </Text>
          <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
            {t("Track your learning progress and assessment results")}
          </Text>
        </View>
      </View>

      {/* Stats Summary Cards */}
      <View style={[
        styles.statsRow,
        { flexDirection: isDesktop ? "row" : "column" }
      ]}>
        <View style={[
          styles.statCard,
          { backgroundColor: isDarkMode ? "#1e293b" : "#fff" },
          isDesktop && { flex: 1 }
        ]}>
          <MaterialCommunityIcons
            name="clipboard-check-outline"
            size={28}
            color={isDarkMode ? "#34d399" : "#10b981"}
          />
          <Text style={[styles.statValue, { color: colors.textPrimary }]}>
            {totalAssessments}
          </Text>
          <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
            {t("Assessments")}
          </Text>
        </View>

        <View style={[
          styles.statCard,
          { backgroundColor: isDarkMode ? "#1e293b" : "#fff" },
          isDesktop && { flex: 1 }
        ]}>
          <MaterialCommunityIcons
            name="target"
            size={28}
            color={isDarkMode ? "#60a5fa" : "#3b82f6"}
          />
          <Text style={[styles.statValue, { color: colors.textPrimary }]}>
            {averageAccuracy}%
          </Text>
          <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
            {t("Avg. Accuracy")}
          </Text>
        </View>

        <View style={[
          styles.statCard,
          { backgroundColor: isDarkMode ? "#1e293b" : "#fff" },
          isDesktop && { flex: 1 }
        ]}>
          <MaterialCommunityIcons
            name="star-outline"
            size={28}
            color={isDarkMode ? "#fbbf24" : "#f59e0b"}
          />
          <Text style={[styles.statValue, { color: colors.textPrimary }]}>
            {totalScore}/{maxPossibleScore}
          </Text>
          <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
            {t("Total Score")}
          </Text>
        </View>
      </View>

      {/* Adaptive Recommendation */}
      {adaptiveRecommendation && (
        <View style={[
          styles.recommendationCard,
          { backgroundColor: isDarkMode ? "rgba(99, 102, 241, 0.15)" : "rgba(99, 102, 241, 0.08)" }
        ]}>
          <MaterialCommunityIcons
            name="lightbulb-outline"
            size={24}
            color={isDarkMode ? "#818cf8" : "#6366f1"}
          />
          <View style={styles.recommendationContent}>
            <Text style={[styles.recommendationTitle, { color: isDarkMode ? "#818cf8" : "#6366f1" }]}>
              {t("Personalized Recommendation")}
            </Text>
            <Text style={[styles.recommendationText, { color: colors.textPrimary }]}>
              {adaptiveRecommendation}
            </Text>
          </View>
        </View>
      )}

      {/* Assessment History */}
      <View style={styles.section}>
        <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>
          {t("Assessment History")}
        </Text>

        {assessments.length > 0 ? (
          <View style={[
            styles.assessmentGrid,
            { flexDirection: isDesktop ? "row" : "column", flexWrap: isDesktop ? "wrap" : "nowrap" }
          ]}>
            {assessments.map((assessment, index) => (
              <View
                key={index}
                style={[
                  styles.assessmentCard,
                  { backgroundColor: isDarkMode ? "#1e293b" : "#fff" },
                  isDesktop && { width: "48%" }
                ]}
              >
                <View style={styles.assessmentHeader}>
                  <Text style={[styles.assessmentSubject, { color: colors.textPrimary }]}>
                    {assessment.subject}
                  </Text>
                  <View style={[
                    styles.scoreBadge,
                    { backgroundColor: getScoreColor(assessment.accuracy, isDarkMode) }
                  ]}>
                    <Text style={styles.scoreBadgeText}>
                      {assessment.accuracy}%
                    </Text>
                  </View>
                </View>

                <Text style={[styles.assessmentMeta, { color: colors.textSecondary }]}>
                  {assessment.chapter} - {assessment.topic}
                </Text>

                <View style={styles.assessmentFooter}>
                  <View style={styles.assessmentScore}>
                    <MaterialCommunityIcons
                      name="check-circle-outline"
                      size={16}
                      color={isDarkMode ? "#34d399" : "#10b981"}
                    />
                    <Text style={[styles.assessmentScoreText, { color: colors.textPrimary }]}>
                      {assessment.score}/{assessment.total_score}
                    </Text>
                  </View>
                  <Text style={[styles.assessmentDate, { color: colors.textSecondary }]}>
                    {assessment.completed_at
                      ? formatLocalDate(assessment.completed_at)
                      : "N/A"}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        ) : (
          <View style={[styles.emptyState, { backgroundColor: isDarkMode ? "#1e293b" : "#fff" }]}>
            <MaterialCommunityIcons
              name="chart-line"
              size={48}
              color={isDarkMode ? "#475569" : "#cbd5e1"}
            />
            <Text style={[styles.emptyStateTitle, { color: colors.textPrimary }]}>
              {t("No assessments yet")}
            </Text>
            <Text style={[styles.emptyStateText, { color: colors.textSecondary }]}>
              {t("Complete quizzes and tests to see your performance analytics here.")}
            </Text>
          </View>
        )}
      </View>
    </ScrollView>
  );
};

// Helper to get score badge color
const getScoreColor = (accuracy, isDarkMode) => {
  if (accuracy >= 80) return isDarkMode ? "rgba(52, 211, 153, 0.3)" : "rgba(16, 185, 129, 0.15)";
  if (accuracy >= 60) return isDarkMode ? "rgba(251, 191, 36, 0.3)" : "rgba(245, 158, 11, 0.15)";
  return isDarkMode ? "rgba(248, 113, 113, 0.3)" : "rgba(239, 68, 68, 0.15)";
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: 24,
    paddingBottom: 100,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 24,
    gap: 16,
  },
  headerIcon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    backgroundColor: "rgba(99, 102, 241, 0.1)",
    justifyContent: "center",
    alignItems: "center",
  },
  headerText: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: "700",
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
  },
  statsRow: {
    gap: 12,
    marginBottom: 24,
  },
  statCard: {
    padding: 20,
    borderRadius: 16,
    alignItems: "center",
    gap: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  statValue: {
    fontSize: 28,
    fontWeight: "700",
  },
  statLabel: {
    fontSize: 13,
  },
  recommendationCard: {
    flexDirection: "row",
    padding: 16,
    borderRadius: 12,
    marginBottom: 24,
    gap: 12,
    alignItems: "flex-start",
  },
  recommendationContent: {
    flex: 1,
  },
  recommendationTitle: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 4,
  },
  recommendationText: {
    fontSize: 14,
    lineHeight: 20,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 16,
  },
  assessmentGrid: {
    gap: 12,
  },
  assessmentCard: {
    padding: 16,
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
    marginBottom: 12,
  },
  assessmentHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  assessmentSubject: {
    fontSize: 16,
    fontWeight: "600",
    flex: 1,
  },
  scoreBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  scoreBadgeText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#fff",
  },
  assessmentMeta: {
    fontSize: 13,
    marginBottom: 12,
  },
  assessmentFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  assessmentScore: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  assessmentScoreText: {
    fontSize: 14,
    fontWeight: "500",
  },
  assessmentDate: {
    fontSize: 12,
  },
  emptyState: {
    padding: 40,
    borderRadius: 16,
    alignItems: "center",
    gap: 12,
  },
  emptyStateTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  emptyStateText: {
    fontSize: 14,
    textAlign: "center",
    maxWidth: 280,
  },
});

export default StudentAnalyticsPage;
